import { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ArrowUpRight, ChevronDown, Globe, Plus } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hostOf = (url) => url.replace(/^https?:\/\//, "").split("/")[0];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/projects`).then((res) => setProjects(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="projects-page">
      <p className="font-mono text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-amber-700">Projects</p>
      <h1 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900" data-testid="projects-heading">
        Small tools, built to solve real problems
      </h1>
      <p className="mt-4 max-w-2xl text-base sm:text-lg text-stone-600 leading-relaxed">
        Things I've designed and shipped myself — usually because I needed them and couldn't find anything that fit.
      </p>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8" data-testid="projects-grid">
        {loading && <p className="text-stone-500" data-testid="projects-loading">Loading projects...</p>}
        {projects.map((p) => {
          const isOpen = expandedId === p.id;
          return (
            <div
              key={p.id}
              data-testid={`project-card-${p.id}`}
              className="group rounded-3xl border border-stone-200 bg-white p-8 sm:p-10 shadow-sm transition-all hover:shadow-md hover:border-amber-300"
            >
              <button
                type="button"
                onClick={() => setExpandedId(isOpen ? null : p.id)}
                data-testid={`project-toggle-${p.id}`}
                className="w-full text-left"
                aria-expanded={isOpen}
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100">
                    <Globe className="h-6 w-6 text-amber-700" />
                  </div>
                  <ChevronDown className={`h-6 w-6 text-stone-300 transition-transform group-hover:text-amber-600 ${isOpen ? "rotate-180" : ""}`} />
                </div>
                {p.category && <p className="mt-6 font-mono text-xs font-medium uppercase tracking-widest text-amber-700">{p.category}</p>}
                <h2 className="mt-2 font-heading text-xl sm:text-2xl font-semibold text-stone-900" data-testid={`project-title-${p.id}`}>
                  {p.title}
                </h2>
                <p className="mt-3 text-base text-stone-600 leading-relaxed">{p.description}</p>
              </button>

              {isOpen && (p.story || p.gallery?.length > 0) && (
                <div className="mt-6 border-t border-stone-100 pt-6 space-y-6" data-testid={`project-story-${p.id}`}>
                  {p.gallery?.length > 0 && (
                    <Carousel className="w-full" data-testid={`project-gallery-${p.id}`}>
                      <CarouselContent>
                        {p.gallery.map((src, i) => (
                          <CarouselItem key={i}>
                            <img
                              src={src}
                              alt={`${p.title} screenshot ${i + 1}`}
                              className="w-full aspect-video object-cover rounded-2xl border border-stone-200"
                            />
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      {p.gallery.length > 1 && (
                        <>
                          <CarouselPrevious className="left-2" />
                          <CarouselNext className="right-2" />
                        </>
                      )}
                    </Carousel>
                  )}
                  {p.story && (
                    <div className="markdown-body text-sm sm:text-base [&_p]:mb-4 [&_h2]:text-base [&_h2]:font-heading [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:first:mt-0 [&_h2]:uppercase [&_h2]:tracking-wide [&_h2]:text-amber-700">
                      <ReactMarkdown>{p.story}</ReactMarkdown>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm font-semibold text-amber-700">{hostOf(p.url)}</p>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`project-try-link-${p.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-amber-700"
                >
                  Try it out <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          );
        })}

        {!loading && (
          <div
            className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-stone-200 p-8 sm:p-10 text-center"
            data-testid="project-card-placeholder"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-100">
              <Plus className="h-6 w-6 text-stone-400" />
            </div>
            <h2 className="mt-6 font-heading text-xl font-semibold text-stone-500">More coming soon</h2>
            <p className="mt-2 max-w-xs text-sm text-stone-400 leading-relaxed">
              There's always another small tool in the works. Check back soon — or follow along on the blog.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
